// deployment_class: customer_env_allowed
/**
 * Template actions in the Portal dropdown — New / Save / Save As.
 *
 * ── Why it is collapsed, and admin-only (E3 2026-08-29) ────────────────────
 * E3: *"with the save template, new template menu items, let's have those
 * expandable. Like, let's not have them always visible in terms of the user.
 * Like, the CEO most likely will never want to see that. So let's have that for
 * admins only."*
 *
 * Two separate decisions, and both matter:
 *
 * 1. ADMIN-ONLY — gated on `internalops.read`, the same scope that gates
 *    developer mode itself. A PHA seat never holds it, so this renders NOTHING
 *    for a CEO: not greyed, not disabled, absent. Authoring a template is
 *    changing what the product IS, which is not a customer's action.
 *
 * 2. COLLAPSED BY DEFAULT — even for an admin. Switching portals is the thing
 *    this menu is for and happens many times a day; authoring a template is
 *    rare and deliberate. Putting them at the same level would make the common
 *    action harder to hit in order to make the rare one easier, which is the
 *    wrong trade. It sits behind one disclosure, below a divider.
 *
 * ── Why it is outside the listbox ──────────────────────────────────────────
 * The portal list is `role="listbox"` with `role="option"` children. A listbox
 * must contain only options — putting buttons inside it breaks the semantics
 * and trips axe. So this is a sibling group beneath it, sharing the panel
 * visually while staying its own thing structurally.
 */
import { useEffect, useState } from 'react';
import { useAuth } from '../auth/useAuth';
import { isDeveloper } from '../../dashboard/TemplateEditor';
import { useTemplateStore } from '../../dashboard/template-store';
import { deleteTemplate, listTemplates, type TemplateSummary } from '../../dashboard/template-api';

export function TemplateMenu({ onDone }: { onDone: () => void }): JSX.Element | null {
  const { scopes } = useAuth();
  const { template, newTemplate, save, saveAs, setEditing, open, serverId, saveError } =
    useTemplateStore();
  const [expanded, setExpanded] = useState(false);
  const [naming, setNaming] = useState<null | 'new' | 'saveAs'>(null);
  const [draft, setDraft] = useState('');
  const [library, setLibrary] = useState<TemplateSummary[] | null>(null);

  /**
   * The library is fetched when the menu OPENS, not on mount.
   *
   * A template list is only ever looked at from inside this disclosure, and the
   * menu lives in the shell's top bar on every page — fetching on mount would
   * put a request on every navigation to populate a list nobody opened.
   *
   * Refetched on each open rather than cached: another admin may have authored
   * one since, and a stale library that silently omits a colleague's template is
   * worse than a moment's wait.
   */
  useEffect(() => {
    if (!expanded) return;
    let live = true;
    void listTemplates()
      .then((rows) => {
        if (live) setLibrary(rows);
      })
      // An empty library still renders — the menu's other actions work without
      // it, so a failed list must not take them down with it.
      .catch(() => {
        if (live) setLibrary([]);
      });
    return () => {
      live = false;
    };
  }, [expanded, serverId]);

  // Absent for a customer seat. Nothing below this line reaches a CEO.
  if (!isDeveloper(scopes)) return null;

  const remove = (id: string): void => {
    void deleteTemplate(id)
      .then(() => listTemplates())
      .then(setLibrary)
      .catch(() => setLibrary(library));
  };

  const commit = (): void => {
    if (draft.trim() === '') return;
    if (naming === 'new') newTemplate(draft);
    else saveAs(draft);
    setNaming(null);
    setDraft('');
    onDone();
  };

  return (
    <div className="kd-tplmenu" role="group" aria-label="Template">
      <button
        type="button"
        className="kd-tplmenu-toggle"
        aria-expanded={expanded}
        onClick={() => setExpanded((v) => !v)}
      >
        <span aria-hidden="true" className="kd-tplmenu-caret">
          {expanded ? '▾' : '▸'}
        </span>
        Template
        <span className="kd-tplmenu-name">{template.name}</span>
      </button>

      {expanded && (
        <div className="kd-tplmenu-body">
          {naming === null ? (
            <>
              <button
                type="button"
                onClick={() => {
                  setDraft('');
                  setNaming('new');
                }}
              >
                New template…
              </button>
              <button
                type="button"
                onClick={() => {
                  save();
                  onDone();
                }}
              >
                Save template
              </button>
              <button
                type="button"
                onClick={() => {
                  setDraft(template.name);
                  setNaming('saveAs');
                }}
              >
                Save template as…
              </button>
              <button
                type="button"
                onClick={() => {
                  setEditing(true);
                  onDone();
                }}
              >
                Edit this template
              </button>

              {/*
                The library. Present only once the list has arrived, and only if
                it holds more than the template already open — a one-row list
                whose row is the current template is a control that does nothing.

                A STOCK row is labelled and cannot be deleted: it is the
                product's, not this customer's, and the server refuses the
                delete regardless. Showing a button that always fails would be
                the inverse of a hidden button — visible, and a lie.
              */}
              {library && library.length > 0 && (
                <div className="kd-tplmenu-library" role="group" aria-label="Saved templates">
                  <p className="kd-tplmenu-libhead">Open a template</p>
                  {library.map((t) => (
                    <div key={t.id} className="kd-tplmenu-libitem">
                      <button
                        type="button"
                        aria-current={t.id === serverId ? 'true' : undefined}
                        onClick={() => {
                          open(t.id);
                          onDone();
                        }}
                      >
                        {t.name}
                        {t.isStock && <span className="kd-tplmenu-stock"> · shipped</span>}
                      </button>
                      {!t.isStock && (
                        <button
                          type="button"
                          className="kd-tplmenu-libdel"
                          aria-label={`Delete ${t.name}`}
                          onClick={() => remove(t.id)}
                        >
                          ×
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* A failed save says so. Silence would let someone believe an
                  afternoon's arrangement was stored when it was not. */}
              {saveError && (
                <p className="kd-tplmenu-error" role="alert">
                  {saveError}
                </p>
              )}
            </>
          ) : (
            <div className="kd-tplmenu-name-form">
              <label htmlFor="kd-tpl-name">
                {naming === 'new' ? 'Name the new template' : 'Save a copy as'}
              </label>
              <input
                id="kd-tpl-name"
                type="text"
                value={draft}
                autoFocus
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') commit();
                  if (e.key === 'Escape') setNaming(null);
                }}
              />
              <div className="kd-tplmenu-name-actions">
                <button type="button" disabled={draft.trim() === ''} onClick={commit}>
                  {naming === 'new' ? 'Create' : 'Save as'}
                </button>
                <button type="button" onClick={() => setNaming(null)}>
                  Cancel
                </button>
              </div>
              {naming === 'new' && (
                <p className="kd-tplmenu-note">
                  Starts from the shipped shape — not from this template, so nothing is
                  inherited by accident.
                </p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
